import Guid from "_shared/guids/guid";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import TeamTemplateQuerySchema, { 
  TeamTemplateFilterParametersJSON, 
  TeamTemplateFilterParametersParams, 
  TeamTemplateFilterParametersSchema, 
  TeamTemplateOrderParametersJSON, 
  TeamTemplateOrderParametersParams, 
  TeamTemplateOrderParametersSchema, 
  TeamTemplateQueryJSON, 
  TeamTemplateQueryParams 
} from "work/team-templates/_api/queries/_schema/team-template-query-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";


export default class TeamTemplateQuery {
  public static create(params: TeamTemplateQueryParams): TeamTemplateQuery {
    const parsedParams = TeamTemplateQuerySchema.validate(params);
    
    return new TeamTemplateQuery({
      filterParameters: parsedParams.filterParameters,
      orderParameters: parsedParams.orderParameters,
      paginationParameters: parsedParams.paginationParameters,
    });
  }

  public static fromJSON(json: unknown): TeamTemplateQuery | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsedParams = TeamTemplateQuerySchema.deserialize(json);
    if (!parsedParams) return parsedParams;
    return new TeamTemplateQuery(parsedParams);
  }

  public filterParameters: TeamTemplateFilterParameters;
  public orderParameters: TeamTemplateOrderParameters;
  public paginationParameters: PaginationParameters;

  constructor(params: TeamTemplateQueryParams) {
    this.filterParameters = params.filterParameters ?? TeamTemplateFilterParameters.create({});
    this.orderParameters = params.orderParameters ?? TeamTemplateOrderParameters.create({
      orderBy: TeamTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    this.paginationParameters = params.paginationParameters ?? PaginationParameters.DEFAULT;
  }

  get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.paginationParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.filterParameters.searchParams,
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  isEqualTo(other: TeamTemplateQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }

  public toJSON(): TeamTemplateQueryJSON {
    return TeamTemplateQuerySchema.serialize(this);
  }
}


export class TeamTemplateFilterParameters {
  public static create(params: TeamTemplateFilterParametersParams): TeamTemplateFilterParameters {
    const parsed = TeamTemplateFilterParametersSchema.validate(params);
    return new TeamTemplateFilterParameters(parsed);
  }

  public static fromJSON(json: TeamTemplateFilterParametersJSON | null | undefined): TeamTemplateFilterParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = TeamTemplateFilterParametersSchema.deserialize(json);
    if (parsed) {
      return new TeamTemplateFilterParameters(parsed);
    }
    return parsed;
  }

  public readonly minCreated?: AHDate;
  public readonly maxCreated?: AHDate;
  public readonly name?: string;
  public readonly creatorUserId?: Guid;
  public readonly creatorEntityId?: Guid;
  public readonly leaderUserId?: Guid;
  public readonly leaderEntityId?: Guid;
  public readonly teamMemberUserId?: Guid;
  public readonly teamMemberEntityId?: Guid;
  public readonly marketplaceId?: Guid;
  public readonly isArchived?: boolean;
  public readonly minArchived?: AHDate;
  public readonly maxArchived?: AHDate;

  constructor(params: TeamTemplateFilterParametersParams) {
    this.minCreated = params.minCreated ?? undefined;
    this.maxCreated = params.maxCreated ?? undefined;
    this.name = params.name ?? undefined;
    this.creatorUserId = params.creatorUserId ?? undefined;
    this.creatorEntityId = params.creatorEntityId ?? undefined;
    this.leaderUserId = params.leaderUserId ?? undefined;
    this.leaderEntityId = params.leaderEntityId ?? undefined;
    this.teamMemberUserId = params.teamMemberUserId ?? undefined;
    this.teamMemberEntityId = params.teamMemberEntityId ?? undefined;
    this.marketplaceId = params.marketplaceId ?? undefined;
    this.isArchived = params.isArchived ?? false;
    this.minArchived = params.minArchived ?? undefined;
    this.maxArchived = params.maxArchived ?? undefined;
  }

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (this.minCreated) {
      searchParams.append("minCreated", this.minCreated.toISOString());
    }

    if (this.maxCreated) {
      searchParams.append("maxCreated", this.maxCreated.toISOString());
    }

    if (this.name) {
      searchParams.append("name", this.name);
    }

    if (this.creatorUserId) {
      searchParams.append("creatorUserId", this.creatorUserId.value);
    }

    if (this.creatorEntityId) {
      searchParams.append("creatorEntityId", this.creatorEntityId.value);
    }

    if (this.leaderUserId) {
      searchParams.append("leaderUserId", this.leaderUserId.value);
    }

    if (this.leaderEntityId) {
      searchParams.append("leaderEntityId", this.leaderEntityId.value);
    }

    if (this.teamMemberUserId) {
      searchParams.append("teamMemberUserId", this.teamMemberUserId.value);
    }

    if (this.teamMemberEntityId) {
      searchParams.append("teamMemberEntityId", this.teamMemberEntityId.value);
    }

    if (this.marketplaceId) {
      searchParams.append("marketplaceId", this.marketplaceId.value);
    }

    if (this.isArchived !== undefined) {
      searchParams.append("isArchived", String(this.isArchived));
    }

    if (this.minArchived) {
      searchParams.append("minArchived", this.minArchived.toISOString());
    }

    if (this.maxArchived) {
      searchParams.append("maxArchived", this.maxArchived.toISOString());
    }
    
    return searchParams;
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  public isEqualTo(other: TeamTemplateFilterParameters): boolean {
    if (!other) return false;
    return (
      (!!this.minCreated === !!other.minCreated
        ? this.minCreated?.isEqualTo(other.minCreated) ?? true
        : false)
      && (!!this.maxCreated === !!other.maxCreated
        ? this.maxCreated?.isEqualTo(other.maxCreated) ?? true
        : false)
      && this.name === other.name
      && (!!this.creatorUserId === !!other.creatorUserId
        ? this.creatorUserId?.isEqualTo(other.creatorUserId) ?? true
        : false)
      && (!!this.creatorEntityId === !!other.creatorEntityId
        ? this.creatorEntityId?.isEqualTo(other.creatorEntityId) ?? true
        : false)
      && (!!this.leaderUserId === !!other.leaderUserId
        ? this.leaderUserId?.isEqualTo(other.leaderUserId) ?? true
        : false)
      && (!!this.leaderEntityId === !!other.leaderEntityId
        ? this.leaderEntityId?.isEqualTo(other.leaderEntityId) ?? true
        : false)
      && (!!this.teamMemberUserId === !!other.teamMemberUserId
        ? this.teamMemberUserId?.isEqualTo(other.teamMemberUserId) ?? true
        : false)
      && (!!this.teamMemberEntityId === !!other.teamMemberEntityId
        ? this.teamMemberEntityId?.isEqualTo(other.teamMemberEntityId) ?? true
        : false)
      && (!!this.marketplaceId === !!other.marketplaceId
        ? this.marketplaceId?.isEqualTo(other.marketplaceId) ?? true
        : false)
      && this.isArchived === other.isArchived
      && (!!this.minArchived === !!other.minArchived
        ? this.minArchived?.isEqualTo(other.minArchived) ?? true
        : false)
      && (!!this.maxArchived === !!other.maxArchived
        ? this.maxArchived?.isEqualTo(other.maxArchived) ?? true
        : false)
    );
  }

  public toJSON(): TeamTemplateFilterParametersJSON {
    return TeamTemplateFilterParametersSchema.serialize(this);
  }
}

export class TeamTemplateOrderParameters {
  public static create(params: TeamTemplateOrderParametersParams): TeamTemplateOrderParameters {
    const parsed = TeamTemplateOrderParametersSchema.validate(params);
    return new TeamTemplateOrderParameters(parsed);
  }

  public static fromJSON(json: unknown): TeamTemplateOrderParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = TeamTemplateOrderParametersSchema.validate(json);
    return new TeamTemplateOrderParameters(parsed);
  }

  public readonly orderBy: TeamTemplateOrderField;
  public readonly orderDir: OrderDirection;

  constructor(params: TeamTemplateOrderParametersParams) {
    this.orderBy = params.orderBy ?? TeamTemplateOrderField.Name;
    this.orderDir = params.orderDir ?? OrderDirection.Descending;
  }

  get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (this.orderBy) {
      searchParams.append("orderBy", this.orderBy);
    }

    if (this.orderDir) {
      searchParams.append("orderDir", this.orderDir);
    }

    return searchParams;
  }

  public isEqualTo(other: TeamTemplateOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }

  public toJSON(): TeamTemplateOrderParametersJSON {
    return TeamTemplateOrderParametersSchema.serialize(this);
  }

  public toString(): string {
    return this.searchParams.toString();
  }
}